// Administration → Liste de valori: value lists and their items, with import/export.
import React, { useEffect, useRef, useState } from 'react';
import { api, getToken } from '../../api';
import type { ListItem, ValueList } from '../../types';
import { Confirm, EmptyState, Field, Modal, Switch, useToast } from '../../ui';
import { Icon } from '../../ui/Icon';
import { Loading, PageHead } from './common';

export function ListsPage() {
  const toast = useToast();
  const [lists, setLists] = useState<ValueList[] | null>(null);
  const [selId, setSelId] = useState<number | null>(null);
  const [sel, setSel] = useState<ValueList | null>(null);
  const [newList, setNewList] = useState(false);
  const [rename, setRename] = useState(false);
  const [importing, setImporting] = useState(false);
  const [delList, setDelList] = useState(false);
  const [delItem, setDelItem] = useState<ListItem | null>(null);
  const [add, setAdd] = useState({ value: '', label: '', color: '', days: '' });
  const [edit, setEdit] = useState<{ id: number; value: string; label: string; color: string; sortOrder: number; days: string } | null>(null);

  const loadLists = async (keep = true) => {
    try {
      const ls = await api.get<ValueList[]>('/api/catalog/lists');
      setLists(ls);
      if (!keep || selId == null || !ls.some(l => l.id === selId)) setSelId(ls[0]?.id ?? null);
    } catch (e: any) { toast(e.message, 'error'); }
  };
  const loadSel = async () => {
    if (selId == null) { setSel(null); return; }
    try { setSel(await api.get<ValueList>('/api/catalog/lists/' + selId)); } catch (e: any) { toast(e.message, 'error'); }
  };
  useEffect(() => { loadLists(); }, []);
  useEffect(() => { setEdit(null); loadSel(); }, [selId]);
  const refresh = () => { loadSel(); loadLists(); };

  const run = async (fn: () => Promise<any>, okMsg?: string) => {
    try { await fn(); if (okMsg) toast(okMsg, 'ok'); refresh(); return true; } catch (e: any) { toast(e.message, 'error'); return false; }
  };

  const addItem = async () => {
    if (!add.value.trim() || !sel) return;
    if (await run(() => api.post(`/api/catalog/lists/${sel.id}/items`, { value: add.value.trim(), label: add.label.trim() || undefined, color: add.color || undefined, deadlineDays: add.days ? Number(add.days) : undefined }))) setAdd({ value: '', label: '', color: '', days: '' });
  };
  const saveEdit = async () => {
    if (!edit || !sel) return;
    if (await run(() => api.put(`/api/catalog/lists/${sel.id}/items/${edit.id}`, { value: edit.value.trim(), label: edit.label.trim(), color: edit.color || null, sortOrder: edit.sortOrder, deadlineDays: edit.days === '' ? null : Number(edit.days) }), 'Valoare salvată')) setEdit(null);
  };

  const exportUrl = (fmt: 'json' | 'csv') => `/api/catalog/lists/${sel!.id}/export?format=${fmt}&token=${encodeURIComponent(getToken() || '')}`;
  const items = [...(sel?.items || [])].sort((a, b) => a.sortOrder - b.sortOrder || a.id - b.id);

  return (
    <>
      <PageHead title="Liste de valori" text="Valorile predefinite folosite de atributele de tip listă.">
        <button className="btn primary" onClick={() => setNewList(true)}><Icon name="plus" />Listă nouă</button>
      </PageHead>
      {!lists ? <Loading /> : lists.length === 0 ? <EmptyState icon="list" title="Nicio listă" text="Creați o listă de valori pentru a o folosi într-un atribut." /> : (
        <div className="split">
          <div className="card" style={{ padding: 8 }}>
            <div className="list-nav">
              {lists.map(l => (
                <button key={l.id} className={l.id === selId ? 'active' : ''} onClick={() => setSelId(l.id)}>
                  {l.system ? <Icon name="lock" size={14} /> : <Icon name="list" size={14} />}
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{l.name}</span>
                  <span className="n">{l.itemCount ?? ''}</span>
                </button>
              ))}
            </div>
          </div>
          {!sel ? <Loading /> : (
            <div className="card">
              <div className="card-head">
                <div style={{ minWidth: 0 }}>
                  <h2 className="row" style={{ gap: 6 }}>{sel.name}{sel.system && <span className="tag" title="Listă de sistem"><Icon name="lock" size={11} /> sistem</span>}<button className="icon-btn" title="Redenumește" onClick={() => setRename(true)}><Icon name="edit" size={15} /></button></h2>
                  <span className="muted small"><span className="code">{sel.code}</span> · {items.length} valori</span>
                </div>
                <span className="grow" />
                <button className="btn sm" onClick={() => setImporting(true)}><Icon name="upload" />Import</button>
                <a className="btn sm" href={exportUrl('json')} target="_blank" rel="noopener"><Icon name="download" />Export JSON</a>
                <a className="btn sm" href={exportUrl('csv')} target="_blank" rel="noopener"><Icon name="download" />Export CSV</a>
                {!sel.system && <button className="btn sm danger" onClick={() => setDelList(true)}><Icon name="trash" />Șterge lista</button>}
              </div>
              <div className="card-body" style={{ paddingBottom: 8 }}>
                <Switch on={sel.allowAdd} label="Permite adăugarea de valori noi de către utilizatori (din formularul de document)" onChange={v => run(() => api.put('/api/catalog/lists/' + sel.id, { allowAdd: v }))} />
              </div>
              <div className="table-wrap" style={{ border: 0, borderTop: '1px solid var(--line)', borderRadius: 0 }}>
                <table className="table adm-items">
                  <thead><tr><th style={{ width: '28%' }}>Valoare</th><th>Etichetă</th><th style={{ width: 90 }}>Culoare</th><th style={{ width: 110 }} title="Termenul de răspuns calculat automat pentru documentele care au această valoare">Termen (zile)</th><th style={{ width: 70 }}>Activ</th><th style={{ width: 80 }}>Ordine</th><th style={{ width: 90 }} /></tr></thead>
                  <tbody>
                    <tr className="adm-add-row">
                      <td><input className="input" placeholder="Valoare nouă" value={add.value} onChange={e => setAdd({ ...add, value: e.target.value })} onKeyDown={e => { if (e.key === 'Enter') addItem(); }} /></td>
                      <td><input className="input" placeholder="Etichetă (opțional)" value={add.label} onChange={e => setAdd({ ...add, label: e.target.value })} onKeyDown={e => { if (e.key === 'Enter') addItem(); }} /></td>
                      <td><ColorCell value={add.color} onChange={c => setAdd({ ...add, color: c })} /></td>
                      <td><input className="input" type="number" min={0} placeholder="—" value={add.days} onChange={e => setAdd({ ...add, days: e.target.value })} /></td>
                      <td colSpan={2} className="muted small">se adaugă la final</td>
                      <td style={{ textAlign: 'right' }}><button className="btn sm primary" disabled={!add.value.trim()} onClick={addItem}><Icon name="plus" />Adaugă</button></td>
                    </tr>
                    {items.length === 0 && <tr><td colSpan={7} className="muted" style={{ textAlign: 'center', padding: 24 }}>Lista nu are încă valori. Adăugați una mai sus sau importați.</td></tr>}
                    {items.map(it => edit && edit.id === it.id ? (
                      <tr key={it.id}>
                        <td><input className="input" autoFocus value={edit.value} onChange={e => setEdit({ ...edit, value: e.target.value })} onKeyDown={e => { if (e.key === 'Enter') saveEdit(); if (e.key === 'Escape') setEdit(null); }} /></td>
                        <td><input className="input" value={edit.label} onChange={e => setEdit({ ...edit, label: e.target.value })} onKeyDown={e => { if (e.key === 'Enter') saveEdit(); if (e.key === 'Escape') setEdit(null); }} /></td>
                        <td><ColorCell value={edit.color} onChange={c => setEdit({ ...edit, color: c })} /></td>
                        <td><input className="input" type="number" min={0} placeholder="—" value={edit.days} onChange={e => setEdit({ ...edit, days: e.target.value })} /></td>
                        <td>{it.active ? 'Da' : 'Nu'}</td>
                        <td><input className="input" type="number" value={edit.sortOrder} onChange={e => setEdit({ ...edit, sortOrder: Number(e.target.value) })} /></td>
                        <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}><button className="icon-btn" title="Salvează" onClick={saveEdit}><Icon name="check" /></button><button className="icon-btn" title="Renunță" onClick={() => setEdit(null)}><Icon name="x" /></button></td>
                      </tr>
                    ) : (
                      <tr key={it.id} style={{ cursor: 'default', opacity: it.active ? 1 : .55 }}>
                        <td><span className="code">{it.value}</span></td>
                        <td>{it.label}</td>
                        <td>{it.color ? <span className="row" style={{ gap: 6 }}><span className="color-dot" style={{ background: it.color }} /><span className="small muted">{it.color}</span></span> : <span className="muted">—</span>}</td>
                        <td className="num">{it.deadlineDays != null ? `${it.deadlineDays} zile` : <span className="muted">—</span>}</td>
                        <td><Switch on={it.active} onChange={v => run(() => api.put(`/api/catalog/lists/${sel.id}/items/${it.id}`, { active: v }))} /></td>
                        <td className="num">{it.sortOrder}</td>
                        <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                          <button className="icon-btn" title="Editează" onClick={() => setEdit({ id: it.id, value: it.value, label: it.label || '', color: it.color || '', sortOrder: it.sortOrder, days: it.deadlineDays != null ? String(it.deadlineDays) : '' })}><Icon name="edit" /></button>
                          <button className="icon-btn" title="Șterge" onClick={() => setDelItem(it)}><Icon name="trash" /></button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {newList && <ListNameModal title="Listă nouă" initial={{ name: '', allowAdd: true }} onClose={() => setNewList(false)} onSave={async v => {
        try { const l = await api.post<ValueList>('/api/catalog/lists', v); toast('Listă creată', 'ok'); setNewList(false); await loadLists(); setSelId(l.id); } catch (e: any) { toast(e.message, 'error'); }
      }} />}
      {rename && sel && <ListNameModal title="Redenumire listă" initial={{ name: sel.name, allowAdd: sel.allowAdd }} onClose={() => setRename(false)} onSave={async v => {
        if (await run(() => api.put('/api/catalog/lists/' + sel.id, v), 'Listă salvată')) setRename(false);
      }} />}
      {importing && sel && <ImportModal list={sel} onClose={() => setImporting(false)} onDone={() => { setImporting(false); refresh(); }} />}
      {delList && sel && <Confirm title="Ștergere listă" danger yesLabel="Șterge" onNo={() => setDelList(false)} onYes={async () => { if (await run(() => api.del('/api/catalog/lists/' + sel.id), 'Listă ștearsă')) { setDelList(false); loadLists(false); } }}
        text={<>Ștergeți lista <b>{sel.name}</b> cu toate cele {items.length} valori? Atributele care o folosesc vor rămâne fără listă.</>} />}
      {delItem && sel && <Confirm title="Ștergere valoare" danger yesLabel="Șterge" onNo={() => setDelItem(null)} onYes={async () => { if (await run(() => api.del(`/api/catalog/lists/${sel.id}/items/${delItem.id}`))) setDelItem(null); }}
        text={<>Ștergeți valoarea <b>{delItem.label || delItem.value}</b>? Documentele care o folosesc își păstrează valoarea salvată. Dacă doriți doar să o ascundeți, dezactivați-o.</>} />}
    </>
  );
}

function ColorCell({ value, onChange }: { value: string; onChange: (c: string) => void }) {
  return (
    <span className="row" style={{ gap: 6 }}>
      <input type="color" value={value || '#94a3b8'} onChange={e => onChange(e.target.value)} className="adm-color" title="Culoare" />
      {value ? <button type="button" className="icon-btn" style={{ width: 22, height: 22 }} title="Fără culoare" onClick={() => onChange('')}><Icon name="x" size={12} /></button> : <span className="muted small">—</span>}
    </span>
  );
}

function ListNameModal({ title, initial, onClose, onSave }: { title: string; initial: { name: string; allowAdd: boolean }; onClose: () => void; onSave: (v: { name: string; allowAdd: boolean }) => Promise<void> }) {
  const [v, setV] = useState(initial);
  const [busy, setBusy] = useState(false);
  const submit = async () => { if (!v.name.trim()) return; setBusy(true); try { await onSave({ name: v.name.trim(), allowAdd: v.allowAdd }); } finally { setBusy(false); } };
  return (
    <Modal title={title} size="sm" icon="list" onClose={onClose} footer={<><span className="grow" /><button className="btn" onClick={onClose}>Renunță</button><button className="btn primary" disabled={busy || !v.name.trim()} onClick={submit}>Salvează</button></>}>
      <div className="stack">
        <Field label="Nume" required><input className="input" autoFocus value={v.name} onChange={e => setV({ ...v, name: e.target.value })} onKeyDown={e => { if (e.key === 'Enter') submit(); }} placeholder="ex. Tipuri de contracte" /></Field>
        <Switch on={v.allowAdd} onChange={x => setV({ ...v, allowAdd: x })} label="Permite adăugarea de valori noi de către utilizatori" />
      </div>
    </Modal>
  );
}

function ImportModal({ list, onClose, onDone }: { list: ValueList; onClose: () => void; onDone: () => void }) {
  const toast = useToast();
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const readFile = (file: File) => {
    const r = new FileReader();
    r.onload = () => setText(String(r.result || ''));
    r.onerror = () => toast('Fișierul nu a putut fi citit', 'error');
    r.readAsText(file);
  };
  const submit = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try {
      const r = await api.postText<{ imported: number; received: number }>(`/api/catalog/lists/${list.id}/import`, text);
      toast(`${r.imported} valori importate din ${r.received} primite`, 'ok');
      onDone();
    } catch (e: any) { toast(e.message, 'error'); } finally { setBusy(false); }
  };
  const lines = text.split(/\r?\n/).filter(l => l.trim()).length;
  return (
    <Modal title={`Import valori în „${list.name}”`} icon="upload" onClose={onClose} footer={<>
      <span className="muted small">{lines ? `${lines} linii` : ''}</span><span className="grow" />
      <button className="btn" onClick={onClose}>Renunță</button><button className="btn primary" disabled={busy || !text.trim()} onClick={submit}>{busy ? 'Se importă…' : 'Importă'}</button></>}>
      <div className="stack">
        <Field label="Valori" hint="O valoare pe linie, opțional „valoare;etichetă”. Se acceptă și un JSON: listă de texte sau de obiecte {value, label, color}. Valorile existente sunt ignorate.">
          <textarea className="textarea" style={{ minHeight: 200, fontFamily: 'var(--mono)', fontSize: 13 }} autoFocus value={text} onChange={e => setText(e.target.value)} placeholder={'activ;Activ\ninactiv;Inactiv\nsuspendat'} />
        </Field>
        <div className="row">
          <button className="btn" onClick={() => fileRef.current?.click()}><Icon name="folder" />Alege fișier (.txt, .csv, .json)</button>
          <input ref={fileRef} type="file" accept=".txt,.csv,.json,text/plain,text/csv,application/json" style={{ display: 'none' }} onChange={e => { const f = e.target.files?.[0]; if (f) readFile(f); e.target.value = ''; }} />
          <span className="muted small">Fișierul este citit în browser și conținutul apare mai sus.</span>
        </div>
      </div>
    </Modal>
  );
}
